import * as THREE from 'three';
import { SHARED_VERTEX_SHADER, SHARED_FRAGMENT_SHADER } from '../core/ParticleSystem.js';

/**
 * Business Scene - Department clusters visualization
 * Shows Engineering, Marketing, Operations, Research as distinct clusters
 */
export class BusinessScene {
  constructor(count = 5000) {
    this.count = count;
    this.geometry = null;
    this.material = null;
    this.points = null;

    // Department configurations
    this.departments = {
      engineering: { center: [-35, 20, 0], color: [0.0, 0.4, 1.0], count: 1250, behavior: 'grid' },
      marketing: { center: [35, 20, 0], color: [1.0, 0.2, 0.4], count: 1000, behavior: 'flow' },
      operations: { center: [-35, -25, 0], color: [0.2, 1.0, 0.4], count: 1250, behavior: 'linear' },
      research: { center: [35, -25, 0], color: [1.0, 0.6, 0.2], count: 1000, behavior: 'pulse' }
    };

    this.init();
  }

  init() {
    this.geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(this.count * 3);
    const colors = new Float32Array(this.count * 3);
    const sizes = new Float32Array(this.count);
    const phases = new Float32Array(this.count);

    let particleIndex = 0;

    Object.entries(this.departments).forEach(([name, config], deptIndex) => {
      const [cx, cy, cz] = config.center;
      const [r, g, b] = config.color;

      for (let i = 0; i < config.count && particleIndex < this.count; i++) {
        const i3 = particleIndex * 3;

        let x, y, z;
        if (config.behavior === 'grid') {
          const gridSize = 5;
          x = cx + (Math.floor(Math.random() * 8) - 4) * gridSize + (Math.random() - 0.5) * 2;
          y = cy + (Math.floor(Math.random() * 6) - 3) * gridSize + (Math.random() - 0.5) * 2;
          z = cz + (Math.random() - 0.5) * 10;
        } else if (config.behavior === 'flow') {
          const angle = Math.random() * Math.PI * 2;
          const radius = Math.pow(Math.random(), 0.5) * 20;
          x = cx + Math.cos(angle) * radius;
          y = cy + Math.sin(angle) * radius * 0.6;
          z = cz + (Math.random() - 0.5) * 15;
        } else if (config.behavior === 'linear') {
          const track = Math.floor(Math.random() * 5);
          x = cx + (Math.random() - 0.5) * 40;
          y = cy + track * 6 - 12;
          z = cz + (Math.random() - 0.5) * 8;
        } else {
          const nodeIndex = Math.floor(Math.random() * 4);
          const nodeX = cx + (nodeIndex % 2) * 20 - 10;
          const nodeY = cy + Math.floor(nodeIndex / 2) * 15 - 7;
          const spread = Math.pow(Math.random(), 0.5) * 8;
          const angle = Math.random() * Math.PI * 2;
          x = nodeX + Math.cos(angle) * spread;
          y = nodeY + Math.sin(angle) * spread;
          z = cz + (Math.random() - 0.5) * 10;
        }

        positions[i3] = x;
        positions[i3 + 1] = y;
        positions[i3 + 2] = z;

        colors[i3] = r + (Math.random() - 0.5) * 0.15;
        colors[i3 + 1] = g + (Math.random() - 0.5) * 0.15;
        colors[i3 + 2] = b + (Math.random() - 0.5) * 0.15;

        // Wide size variation
        const randomVariation = 0.3 + Math.random() * 3.0;
        sizes[particleIndex] = (0.5 + Math.random() * 10.0) * randomVariation;
        phases[particleIndex] = Math.random() * Math.PI * 2;

        particleIndex++;
      }
    });

    // Fill remaining
    while (particleIndex < this.count) {
      const i3 = particleIndex * 3;
      positions[i3] = (Math.random() - 0.5) * 120;
      positions[i3 + 1] = (Math.random() - 0.5) * 80;
      positions[i3 + 2] = (Math.random() - 0.5) * 40;
      colors[i3] = 0.4;
      colors[i3 + 1] = 0.4;
      colors[i3 + 2] = 0.5;
      const randomVariation = 0.3 + Math.random() * 3.0;
      sizes[particleIndex] = (0.5 + Math.random() * 10.0) * randomVariation;
      phases[particleIndex] = Math.random() * Math.PI * 2;
      particleIndex++;
    }

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    this.geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

    this.uniforms = {
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(0, 0) },
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
      uSize: { value: 60 }
    };

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: this.getVertexShader(),
      fragmentShader: this.getFragmentShader(),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      vertexColors: true
    });

    this.points = new THREE.Points(this.geometry, this.material);
  }

  // Use shared shaders for consistent look
  getVertexShader() {
    return SHARED_VERTEX_SHADER;
  }

  getFragmentShader() {
    return SHARED_FRAGMENT_SHADER;
  }

  update(time, deltaTime) {
    this.uniforms.uTime.value = time;
  }

  updateMouse(x, y) {
    this.uniforms.uMouse.value.set(x, y);
  }

  getMesh() {
    return this.points;
  }

  getPoints() {
    return this.points;
  }

  dispose() {
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
  }
}
